import { useState } from "react";
import { ArrowLeft, Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";

interface ResultsSummaryProps {
  title: string;
  query?: string;
  onBack: () => void;
}

async function copyText(value: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard unavailable");
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  const succeeded = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!succeeded) {
    throw new Error("Clipboard unavailable");
  }
}

export function ResultsSummary({ title, query, onBack }: ResultsSummaryProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!query) {
      return;
    }

    try {
      await copyText(query);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-start gap-3 sm:grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] sm:items-center">
      <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to search" title="Back to search" className="justify-self-start">
        <ArrowLeft className="size-5" />
      </Button>

      <div className="min-w-0 text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{title}</h1>
      </div>

      {query ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCopy}
          aria-label={copied ? "Query copied" : "Copy query"}
          title={copied ? "Query copied" : "Copy query"}
          className="justify-self-end"
        >
          {copied ? <Check className="size-5" /> : <Copy className="size-5" />}
        </Button>
      ) : (
        <div className="size-10 justify-self-end" aria-hidden="true" />
      )}
    </div>
  );
}
